// Telegram's system and content insets describe separate reserved areas.
// https://core.telegram.org/bots/webapps#safeareainset
// https://core.telegram.org/bots/webapps#contentsafeareainset
// The WebView fallback uses the event names in Telegram's official JS SDK:
// https://telegram.org/js/telegram-web-app.js
const EDGES = ['top', 'right', 'bottom', 'left'];
const zeroInsets = () => ({ top: 0, right: 0, bottom: 0, left: 0 });
const validNumber = value => typeof value === 'number' && Number.isFinite(value);
const positive = value => validNumber(value) && value > 0 ? value : undefined;
const inset = (...values) => {
  const value = values.find(validNumber);
  return Math.max(0, value === undefined ? 0 : value);
};
const px = value => String(Math.round(value * 100) / 100) + 'px';

// Merely loading telegram-web-app.js also creates WebApp in a normal browser.
// Neither that object nor browser env(safe-area-inset-*) enables Telegram padding.
export function isTelegramViewport(webApp) {
  return Boolean(webApp && (webApp.initData ||
    (webApp.platform && webApp.platform !== 'unknown')));
}

export function calculateTelegramViewport(webApp, environment = {}) {
  const active = isTelegramViewport(webApp);
  const raw = environment.raw || {};
  const browserHeight = positive(environment.height) || 0;
  const browserWidth = positive(environment.width) || 0;
  const reportedHeight = active
    ? positive(webApp.viewportHeight) || positive(raw.height) || positive(environment.cssHeight) || browserHeight
    : browserHeight;
  const height = Math.min(reportedHeight, positive(environment.visualHeight) || reportedHeight);
  const stableHeight = active
    ? positive(webApp.viewportStableHeight) || positive(raw.stableHeight) || positive(environment.cssStableHeight) || reportedHeight
    : browserHeight;
  const safeArea = zeroInsets();
  const contentSafeArea = zeroInsets();
  const safe = zeroInsets();
  if (active) {
    EDGES.forEach(edge => {
      safeArea[edge] = inset(webApp.safeAreaInset?.[edge], raw.safeArea?.[edge],
        environment.cssSafeArea?.[edge], environment.nativeSafeArea?.[edge]);
      contentSafeArea[edge] = inset(webApp.contentSafeAreaInset?.[edge], raw.contentSafeArea?.[edge],
        environment.cssContentSafeArea?.[edge]);
      safe[edge] = safeArea[edge] + contentSafeArea[edge];
    });
  }
  return {
    active,
    fullscreen: active && (typeof webApp.isFullscreen === 'boolean' ? webApp.isFullscreen : raw.fullscreen === true),
    safeArea,
    contentSafeArea,
    safe,
    height,
    stableHeight,
    availableHeight: Math.max(0, height - safe.top - safe.bottom),
    availableStableHeight: Math.max(0, stableHeight - safe.top - safe.bottom),
    availableWidth: Math.max(0, browserWidth - safe.left - safe.right),
  };
}

export function installTelegramViewport(targetWindow = window, targetDocument = document) {
  const root = targetDocument.documentElement;
  const webApp = targetWindow.Telegram?.WebApp;
  const bridge = targetWindow.Telegram?.WebView;
  const active = isTelegramViewport(webApp);
  const raw = {};
  const removers = [];
  const originalProperties = new Map();
  const originalActive = root.getAttribute('data-telegram-viewport');
  const originalFullscreen = root.getAttribute('data-telegram-fullscreen');
  let disposed = false;
  let frame = null;
  let probe;
  if (active && targetDocument.body) {
    probe = targetDocument.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;width:0;height:0;' +
      'padding-top:env(safe-area-inset-top,0px);padding-right:env(safe-area-inset-right,0px);' +
      'padding-bottom:env(safe-area-inset-bottom,0px);padding-left:env(safe-area-inset-left,0px);';
    targetDocument.body.appendChild(probe);
  }
  const cssNumber = (style, name) => {
    const text = style?.getPropertyValue(name)?.trim() || '';
    return /^\d+(?:\.\d+)?px$/.test(text) ? Number.parseFloat(text) : undefined;
  };
  const setProperty = (name, value) => {
    if (!originalProperties.has(name)) {
      originalProperties.set(name, [root.style.getPropertyValue(name), root.style.getPropertyPriority(name)]);
    }
    root.style.setProperty(name, px(value));
  };
  const update = () => {
    frame = null;
    if (disposed) return;
    const style = targetWindow.getComputedStyle(root);
    const nativeStyle = probe ? targetWindow.getComputedStyle(probe) : null;
    const cssSafeArea = {}, cssContentSafeArea = {}, nativeSafeArea = {};
    EDGES.forEach(edge => {
      cssSafeArea[edge] = cssNumber(style, '--tg-safe-area-inset-' + edge);
      cssContentSafeArea[edge] = cssNumber(style, '--tg-content-safe-area-inset-' + edge);
      nativeSafeArea[edge] = cssNumber(nativeStyle, 'padding-' + edge);
    });
    const value = calculateTelegramViewport(webApp, {
      raw,
      height: targetWindow.innerHeight,
      width: targetWindow.innerWidth,
      visualHeight: targetWindow.visualViewport?.height,
      cssHeight: cssNumber(style, '--tg-viewport-height'),
      cssStableHeight: cssNumber(style, '--tg-viewport-stable-height'),
      cssSafeArea, cssContentSafeArea, nativeSafeArea,
    });
    EDGES.forEach(edge => {
      setProperty('--helpdesk-safe-area-' + edge, value.safeArea[edge]);
      setProperty('--helpdesk-content-safe-area-' + edge, value.contentSafeArea[edge]);
      setProperty('--helpdesk-safe-' + edge, value.safe[edge]);
    });
    setProperty('--helpdesk-viewport-height', value.height);
    setProperty('--helpdesk-viewport-stable-height', value.stableHeight);
    setProperty('--helpdesk-available-height', value.availableHeight);
    setProperty('--helpdesk-available-stable-height', value.availableStableHeight);
    setProperty('--helpdesk-available-width', value.availableWidth);
    root.setAttribute('data-telegram-viewport', String(value.active));
    root.setAttribute('data-telegram-fullscreen', String(value.fullscreen));
    targetWindow.dispatchEvent(new targetWindow.CustomEvent('helpdesk:viewport-changed', { detail: value }));
  };
  const schedule = () => {
    if (disposed || frame !== null) return;
    if (targetWindow.requestAnimationFrame) frame = targetWindow.requestAnimationFrame(update);
    else update();
  };
  const listen = (target, name, handler) => {
    if (!target?.onEvent) return;
    try {
      target.onEvent(name, handler);
      removers.push(() => target.offEvent?.(name, handler));
    } catch {
      // Older SDKs can omit newer events; the official WebView bridge covers them.
    }
  };
  const requestInsets = () => {
    if (!active || !bridge?.postEvent) return;
    ['web_app_request_safe_area', 'web_app_request_content_safe_area'].forEach(name => {
      try { bridge.postEvent(name, false); } catch { /* Host may predate Bot API 8. */ }
    });
  };
  if (active) {
    ['safeAreaChanged', 'contentSafeAreaChanged', 'viewportChanged'].forEach(name => listen(webApp, name, schedule));
    listen(webApp, 'fullscreenChanged', () => { requestInsets(); schedule(); });
    listen(webApp, 'fullscreenFailed', event => {
      if (event?.error === 'ALREADY_FULLSCREEN') raw.fullscreen = true;
      requestInsets(); schedule();
    });
    const eventData = (name, data) => data && typeof data === 'object' ? data : typeof name === 'object' ? name : {};
    listen(bridge, 'safe_area_changed', (name, data) => { raw.safeArea = eventData(name, data); schedule(); });
    listen(bridge, 'content_safe_area_changed', (name, data) => { raw.contentSafeArea = eventData(name, data); schedule(); });
    listen(bridge, 'viewport_changed', (name, data) => {
      const value = eventData(name, data);
      if (positive(value.height)) {
        raw.height = value.height;
        if (value.is_state_stable || !raw.stableHeight) raw.stableHeight = value.height;
      }
      schedule();
    });
    listen(bridge, 'fullscreen_changed', (name, data) => {
      const value = eventData(name, data);
      if (typeof value.is_fullscreen === 'boolean') {
        raw.fullscreen = value.is_fullscreen;
        if (!value.is_fullscreen) {
          raw.contentSafeArea = zeroInsets();
          raw.safeArea = undefined;
        }
      }
      requestInsets(); schedule();
    });
    listen(bridge, 'fullscreen_failed', (name, data) => {
      const value = eventData(name, data);
      if (value.error === 'ALREADY_FULLSCREEN') raw.fullscreen = true;
      requestInsets(); schedule();
    });
    requestInsets();
  }
  const windowEvents = ['resize', 'orientationchange', 'pageshow'];
  windowEvents.forEach(name => targetWindow.addEventListener(name, schedule));
  targetWindow.visualViewport?.addEventListener('resize', schedule);
  update();
  return () => {
    disposed = true;
    if (frame !== null) targetWindow.cancelAnimationFrame?.(frame);
    removers.forEach(remove => { try { remove(); } catch { /* Already detached by host. */ } });
    windowEvents.forEach(name => targetWindow.removeEventListener(name, schedule));
    targetWindow.visualViewport?.removeEventListener('resize', schedule);
    probe?.remove();
    originalProperties.forEach(([value, priority], name) => {
      if (value) root.style.setProperty(name, value, priority);
      else root.style.removeProperty(name);
    });
    if (originalActive === null) root.removeAttribute('data-telegram-viewport');
    else root.setAttribute('data-telegram-viewport', originalActive);
    if (originalFullscreen === null) root.removeAttribute('data-telegram-fullscreen');
    else root.setAttribute('data-telegram-fullscreen', originalFullscreen);
  };
}
